// moduleNotifyBridge.js — the GENERIC notification path for installed
// workspace APPS (manifest v2). The notifications sibling of moduleDataBridge
// / moduleCalendarBridge: `el.call('moduleNotify', …)` lets any installed
// appstore app put a real row into the workspace notification surface (bell
// panel + /inbox — packages/shared Notifications.js / notificationScope.js)
// for members of the CURRENT workspace, with zero per-workspace wiring.
// Injected by loadWorkspaceApps for every workspace with ≥1 installed app
// (2026-08-13 per Nika: "appstore apps also come with widgets + notifications
// support — after installing they are just main part of the workspace
// workflow").
//
// ONE DOOR (2026-10-28, server notify by audience — server
// src/domains/notifications/services/notificationAudience.js): every
// moduleNotify is ONE create through the server's audience door, and the
// SERVER picks the recipients from the workspace's own member list. The
// bridge never decides who is a member:
//   • `toUserIds` and the ids in `to` go out as given. The server resolves a
//     Mongo id AND a legacy external id; this roster carries only one of the
//     two per member, so the old client-side match dropped an app's Mongo id
//     for an account that has a legacy id (the bell gap). A non-member id is
//     refused server-side and counted (`refused`).
//   • An email in `to` becomes that roster member's user id — the address
//     never leaves the bridge; an email not on the roster is dropped.
//   • Groups pass through: toRole, toPermission, toTeam, toMembers (refs from
//     the SDK's notifications.audience()), includeAdmins. The server sends a
//     group only when the app's manifest `notify` declares it
//     (403 notify_not_declared otherwise).
//   • No recipient named → every roster member, the caller included (app
//     events are system alerts — low stock, shift assigned — and on a
//     one-member workspace a skip-self default would make the surface
//     silently unreachable). A roster row with no user id is not reachable.
//   • excludeSelf goes to the server, which knows the caller's id exactly.
//   • metadata.dedupKey (with the stamped appId) keeps ONE open notice per
//     member across repeated runs (a repeat answers `deduped`);
//     moduleNotifyClose({ appId, dedupKey }) closes them for every member.
//     metadata.severity: 'INFO' | 'WARNING' | 'ACTION_REQUIRED'.
//   • The app identity, the workspace id and the href are stamped by the
//     bridge, never the caller; the caller must name one of THIS workspace's
//     installed apps. Realtime delivery: the store's create fires the
//     `notification-created` user-socket emit at the write choke point.
//
// CALL CONTRACT (from module code, e.g. a widget body's action handler):
//   await el.call('moduleNotify', {
//     appId: 'system/warehouse-app',   // REQUIRED — the caller's manifest id
//     title: 'Low stock: Shampoo 250ml', // REQUIRED
//     body: '3 left at Central',          // optional
//     route: '/warehouse',                // optional app route ('/'-prefixed);
//                                         // minted into a workspace-addressed
//                                         // href so the row files under the
//                                         // Workspace tab and clicking it
//                                         // opens the app IN this workspace
//     type: 'module',                     // optional row type (defaults to
//                                         // 'module')
//     to: ['a@x.com', '<userId>'],        // optional — roster emails or user ids
//     toUserIds: ['<userId>'],            // optional — Mongo or legacy user ids
//     toRole: 'editor',                   // optional groups — toRole,
//     toPermission: 'production.plan',    //   toPermission, toTeam, toMembers,
//     toTeam: 'planners',                 //   includeAdmins — each one declared
//     includeAdmins: true,                //   in the manifest `notify`
//     excludeSelf: true,                  // optional — skip the caller
//     metadata: { kind: 'overdue', severity: 'WARNING', dedupKey: 'step-9' }
//                                         // optional (appId + workspaceId are
//                                         // stamped on top)
//   }) → { ok, sent, deduped, refused, recipients } | { ok: false, error }
//
//   await el.call('moduleNotifyClose', { appId, dedupKey })
//     → { ok: true, closed } | { ok: false, error }
//
// Every function returns a plain result object and NEVER throws, so an app's
// UI degrades gracefully (same contract as moduleCalendarBridge).

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const shellWithWorkspacePath = (...args) => _wsShared.functions.withWorkspacePath(...args)
const shellWorkspaceUrlHandle = (...args) => _wsShared.functions.workspaceUrlHandle(...args)

// Fan-out ceiling — the server's own cap per send is the same 200 (server
// notificationAudience MAX_AUDIENCE_RECIPIENTS); this guards the default
// fan-out against a pathological people list ever landing on root.
const MAX_RECIPIENTS = 200

// The member groups the server resolves (server notificationAudience
// AUDIENCE_KEYS; `toUserIds` is built here from `to` + `toUserIds`).
const NOTIFY_GROUP_KEYS = ['toRole', 'toPermission', 'toTeam', 'toMembers']

const _notifyLc = (v) => (v == null ? '' : String(v).toLowerCase())
// User ids are opaque (Mongo _id or a legacy external id) — compared and sent
// as exact strings, never lowercased.
const _notifyIdOf = (v) => (v == null || typeof v === 'object' ? '' : String(v).trim())
const _notifyInstalledIds = (installedAppIds) =>
  (Array.isArray(installedAppIds) ? installedAppIds : []).map(_notifyLc).filter(Boolean)
const _notifyWorkspaceIdOf = (root) => {
  const ws = root && root.workspace
  const id = ws && (ws.id || ws._id)
  return id ? String(id) : ''
}

// The server's refusal as one line: `<code> — <message>` (the SDK error's
// `cause` is the route's error body), else 'notify_failed'.
const _notifyErrorOf = (err) => {
  const cause = err && err.cause && typeof err.cause === 'object' ? err.cause : null
  const code = cause && (cause.code || cause.error) ? String(cause.code || cause.error) : ''
  const message = cause && cause.message ? String(cause.message) : ''
  if (!code) return 'notify_failed'
  return message && message !== code ? code + ' — ' + message : code
}

// One roster entry per member: the email when this viewer may see it (their
// own row; every row for an org-mgmt viewer) and the user id (every row carries
// it — root.people `userId`, raw rows `user_id`).
const _notifyRosterOf = (root) => {
  const people = Array.isArray(root && root.people) ? root.people : []
  const roster = []
  const seen = new Set()
  people.forEach((p) => {
    if (!p || typeof p !== 'object') return
    const email = _notifyLc(p.email)
    const userId = _notifyIdOf(p.userId != null ? p.userId : p.user_id)
    const key = userId ? 'id:' + userId : email ? 'email:' + email : ''
    if (!key || seen.has(key)) return
    seen.add(key)
    roster.push({ email, userId })
  })
  return roster
}

export const buildModuleNotifyBridge = (
  root,
  installedAppIds = [],
  { getSdkFn = shellGetSDK } = {}
) => {
  const ids = _notifyInstalledIds(installedAppIds)

  return async function moduleNotify(opts = {}) {
    try {
      const o = opts && typeof opts === 'object' ? opts : {}
      const appId = _notifyLc(o.appId)
      if (!appId || ids.indexOf(appId) < 0) {
        return { ok: false, error: 'unknown_app — appId must name an installed app' }
      }
      const title = o.title ? String(o.title) : ''
      if (!title) return { ok: false, error: 'title_required' }

      const groups = {}
      NOTIFY_GROUP_KEYS.forEach((k) => {
        if (o[k] !== undefined && o[k] !== null) groups[k] = o[k]
      })
      if (o.includeAdmins === true) groups.includeAdmins = true
      const hasGroups = Object.keys(groups).length > 0

      const roster = _notifyRosterOf(root)
      const to = Array.isArray(o.to) ? o.to : []
      const toUserIds = Array.isArray(o.toUserIds) ? o.toUserIds : []
      let userIds = []
      if (to.length || toUserIds.length || hasGroups) {
        const byEmail = new Map()
        roster.forEach((m) => {
          if (m.email && m.userId) byEmail.set(m.email, m.userId)
        })
        to.forEach((v) => {
          const s = _notifyIdOf(v)
          if (!s) return
          if (s.indexOf('@') < 0) userIds.push(s)
          else if (byEmail.has(_notifyLc(s))) userIds.push(byEmail.get(_notifyLc(s)))
        })
        toUserIds.forEach((v) => {
          const s = _notifyIdOf(v)
          if (s) userIds.push(s)
        })
      } else {
        if (!roster.length) {
          return { ok: false, error: 'no_members — workspace roster not loaded yet' }
        }
        userIds = roster.map((m) => m.userId).filter(Boolean)
      }
      userIds = Array.from(new Set(userIds)).slice(0, MAX_RECIPIENTS)
      if (!userIds.length && !hasGroups) {
        return { ok: false, error: 'no_recipients — none of `to` are workspace members' }
      }

      // Href — workspace-ADDRESSED, not workspace-relative: notificationScope's
      // Workspace tab places a row by its `/w/<org>/<ws>` prefix, and a bare
      // app route ('/bi') would read as "current" in EVERY workspace. Minted
      // from the live org + workspace the app is running in.
      const route = typeof o.route === 'string' && o.route.charAt(0) === '/' ? o.route : null
      const orgSlug = root && root.org && (root.org.slug || root.org.key)
      const wsHandle = shellWorkspaceUrlHandle(root && root.workspace)
      const href = route && orgSlug ? shellWithWorkspacePath(orgSlug, wsHandle, route) : route

      // `workspaceId` names the workspace whose member list the server reads
      // (stamped in metadata, and the SDK's `?workspaceId=`).
      const wsId = _notifyWorkspaceIdOf(root)
      const metadata = {
        ...(o.metadata && typeof o.metadata === 'object' ? o.metadata : {}),
        appId,
        ...(wsId ? { workspaceId: wsId } : {})
      }

      const sdk = getSdkFn()
      if (!sdk || typeof sdk.execute !== 'function') {
        return { ok: false, error: 'sdk_unavailable' }
      }
      const payload = {
        type: (o.type ? String(o.type) : '') || 'module',
        title,
        body: o.body ? String(o.body) : null,
        href: href || null,
        ...(userIds.length ? { toUserIds: userIds } : {}),
        ...groups,
        ...(o.excludeSelf ? { excludeSelf: true } : {}),
        metadata
      }
      let out
      try {
        out = await Promise.resolve().then(() =>
          sdk.execute('workspaceProject.notifications', 'create', {
            payload,
            ...(wsId ? { workspaceId: wsId } : {})
          })
        )
      } catch (err) {
        console.warn('[moduleNotify]', appId, err && err.message)
        return { ok: false, error: _notifyErrorOf(err) }
      }
      const n = (k) => (out && Number.isFinite(Number(out[k])) ? Number(out[k]) : 0)
      const sent = n('sent')
      const deduped = n('deduped')
      return {
        ok: sent + deduped > 0,
        sent,
        deduped,
        refused: n('refused'),
        recipients: n('recipients')
      }
    } catch (err) {
      console.warn('[moduleNotify] failed', err && err.message)
      return { ok: false, error: 'notify_failed' }
    }
  }
}

// moduleNotifyClose — close the OPEN notices this app sent under one dedupKey,
// for every member (read + closedAt server-side; the key is free again). Same
// installed-app gate as moduleNotify; the server checks the app is installed in
// the workspace and the caller is a member of it.
export const buildModuleNotifyCloseBridge = (
  root,
  installedAppIds = [],
  { getSdkFn = shellGetSDK } = {}
) => {
  const ids = _notifyInstalledIds(installedAppIds)

  return async function moduleNotifyClose(opts = {}) {
    try {
      const o = opts && typeof opts === 'object' ? opts : {}
      const appId = _notifyLc(o.appId)
      if (!appId || ids.indexOf(appId) < 0) {
        return { ok: false, error: 'unknown_app — appId must name an installed app' }
      }
      const dedupKey = typeof o.dedupKey === 'string' ? o.dedupKey.trim() : ''
      if (!dedupKey) return { ok: false, error: 'dedupKey_required' }
      const sdk = getSdkFn()
      if (!sdk || typeof sdk.execute !== 'function') {
        return { ok: false, error: 'sdk_unavailable' }
      }
      const wsId = _notifyWorkspaceIdOf(root)
      let out
      try {
        out = await Promise.resolve().then(() =>
          sdk.execute('workspaceProject.notifications', 'close', {
            appId,
            dedupKey,
            ...(wsId ? { workspaceId: wsId } : {})
          })
        )
      } catch (err) {
        console.warn('[moduleNotifyClose]', appId, err && err.message)
        return { ok: false, error: _notifyErrorOf(err) }
      }
      const closed = out && Number.isFinite(Number(out.closed)) ? Number(out.closed) : 0
      return { ok: true, closed }
    } catch (err) {
      console.warn('[moduleNotifyClose] failed', err && err.message)
      return { ok: false, error: 'notify_failed' }
    }
  }
}
